'use client'

import { useSearchParams } from 'next/navigation'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { useEffect, useRef, useState } from 'react'
import { BookingStep, type PaymentStep, useBookingFlow } from '~/stores/booking-flow'
import PaymentBooking from '~/components/payment'
import OverlayLoading from '~/components/ui/overlay-loading'
import { useBookingCart } from '~/stores/booking-cart'
import { useHotels } from '~/hooks/use-hotels'
import BookingSearchListDesktop from './search_list'
import { calculateDurationOfStay } from '~/lib/date-utils'
import { useCancelBooking } from '~/hooks/use-booking'
import type { RoomAvailability } from '~/hooks/type'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'
import { usePaymentData } from '~/hooks/use-payment-data'
import { useAiChannelRooms, useIsAiChannel } from '~/hooks/use-ai-channel-rooms'

export default function BookingDesktop() {
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { isLoading, hotels } = useHotels()
  const searchParams = useSearchParams()
  const { cancelBooking } = useCancelBooking()
  const {
    currentStep: bookingStep,
    currentPaymentStep,
    selectedHotel,
    checkInDate,
    checkOutDate,
    adults,
    kidsOver6,
    kidsUnder6,
    setCurrentStep,
    setCurrentPaymentStep,
    setSearchData,
    clearBookingData,
  } = useBookingFlow()
  const { clearCart, items, totalAmount, addToCart } = useBookingCart()
  const { availability } = useHotelAvailabilityStore()
  const isAiChannel = useIsAiChannel()
  useAiChannelRooms()
  const { paymentData, setPaymentData } = usePaymentData()
  const [timeIn, setTimeIn] = useState('')
  const [timeOut, setTimeOut] = useState('')
  const hotelId = searchParams.get('hotelId')
  const ref = searchParams.get('ref')
  // Guards the "reset to step 1 on a stale in-progress booking" check below
  // so it only ever acts on the FIRST paymentData resolution of this mount,
  // never on a bookingNo that appears later as a legitimate part of this
  // same session (a fresh booking just created, gen-qr refreshing data).
  const hasCheckedInitialBookingRef = useRef(false)
  useEffect(() => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const formatDate = (date) => {
      return date.toISOString().split('T')[0]
    }

    // Get current cached state
    const currentState = useBookingFlow.getState()

    // Clear cart if ref=mhg is present in URL
    const ref = searchParams.get('ref')
    if (ref === 'mhg') {
      clearCart()
    }

    // If we have cached data and are in list or payment step, preserve the state
    if (currentState.currentStep !== 'search' && currentState.selectedHotel && hotelId) {
      return
    }

    // Clear previous data
    // clearBookingData()
    clearCart()

    // If no cached state or in search step, process URL parameters
    const urlHotelId = searchParams.get('hotelId')
    if (urlHotelId) {
      console.group('Setting data from URL parameters')
      const data = {
        hotelId: urlHotelId,
        checkInDate: searchParams.get('checkInDate') || formatDate(today),
        checkOutDate: searchParams.get('checkOutDate') || formatDate(tomorrow),
        adults: parseInt(searchParams.get('adults') || '1', 10),
        kidsOver6: parseInt(searchParams.get('kidsOver6') || '0', 10),
        kidsUnder6: parseInt(searchParams.get('kidsUnder6') || '0', 10),
        roomType: searchParams.get('roomType') || '',
      }
      console.groupEnd()
      setSearchData(data)
      setCurrentStep(BookingStep.LIST)
    } else {
      // Set default values and go to search screen when no hotelId is provided
      const data = {
        hotelId: '',
        checkInDate: formatDate(today),
        checkOutDate: formatDate(tomorrow),
        adults: 1,
        kidsOver6: 0,
        kidsUnder6: 0,
        roomType: '',
      }

      setSearchData(data)
      setCurrentStep(BookingStep.SEARCH)
    }
  }, [searchParams, setSearchData, setCurrentStep, clearBookingData, clearCart])
  useEffect(() => {
    const urlHotelId = searchParams.get('hotelId')
    const epayRes = searchParams.get('epayResult')
    const cancelOrder = searchParams.get('isCancelOrder')
    const bookingNo = searchParams.get('bookingNo')
    const urlAdults = searchParams.get('adults')
    const urlKidsOver6 = searchParams.get('kidsOver6')
    const urlKidsUnder6 = searchParams.get('kidsUnder6')
    const step = searchParams.get('step')
    console.log('active')
    // Handle rooms from URL params
    const roomsParam = searchParams.get('rooms')
    // ref=ai links are handled by useAiChannelRooms (waits for prices)
    if (!isAiChannel && roomsParam && availability && availability.length > 0) {
      try {
        const rooms = JSON.parse(decodeURIComponent(roomsParam))
        console.log('rooms', rooms)
        if (Array.isArray(rooms) && rooms.length > 0) {
          // Clear cart first
          clearCart()

          // Add each room to cart by filtering from availability
          rooms.forEach((roomData) => {
            console.log('roomData', roomData)
            // Find room from availability by roomType
            const availableRoom = availability.find(
              (room) => room.roomCode === roomData.roomCode && room.available > 0
            )
            if (availableRoom) {
              // Create a copy of the available room with updated guest information
              const roomAvailability: RoomAvailability = {
                ...availableRoom,
                roomInfo: {
                  ...availableRoom.roomInfo,
                  baseAdult: roomData.adults,
                  baseChild: roomData.kidsOver6 || 0,
                },
                guests: {
                  adults: roomData.adults || 1,
                  children6To12: roomData.kidsOver6 || 0,
                  childrenUnder6: roomData.kidsUnder6 || 0,
                },
              }
              console.log('roomAvailability', roomAvailability)
              // Add to cart with guest information
              const customerCode = hotels?.find(
                (h) => h.hotelId === roomAvailability.hotelId
              )?.customerCode
              addToCart(roomAvailability, 1, customerCode)
            } else {
              console.warn(
                `Room type ${roomData.roomType} not found in availability or not available`
              )
            }
          })

          // Set step to payment if rooms are provided
          // setCurrentStep(BookingStep.PAYMENT)
          // setCurrentPaymentStep(1)
        }
      } catch (error) {
        console.error('Error parsing rooms from URL:', error)
      }
    }

    if (epayRes) {
      if (cancelOrder) {
        cancelBooking({
          hotelId: Number(urlHotelId),
          bookingId: bookingNo || '',
          reason: 'User cancelled the booking',
        })
        setCurrentStep(BookingStep.LIST)
        setCurrentPaymentStep(1)
        const params = new URLSearchParams()
        params.set('hotelId', urlHotelId || '')
        params.set('checkInDate', checkInDate || '')
        params.set('checkOutDate', checkOutDate || '')
        params.set('adults', String(adults || urlAdults || 1))
        params.set('kidsOver6', String(kidsOver6 || urlKidsOver6 || 0))
        params.set('kidsUnder6', String(kidsUnder6 || urlKidsUnder6 || 0))
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
      } else {
        setCurrentStep(BookingStep.PAYMENT)
        const stepNumber = Number(step || '3')
        if (stepNumber >= 1 && stepNumber <= 3) {
          const currentState = useBookingFlow.getState()
          if (stepNumber >= currentState.currentPaymentStep) {
            setCurrentPaymentStep(stepNumber as PaymentStep)
          }
        }
      }
    } else {
      localStorage.removeItem('PaymentDeadline')
      if (step) {
        const stepNumber = Number(step || '3')
        setCurrentStep(BookingStep.PAYMENT)
        setCurrentPaymentStep(stepNumber as PaymentStep)
      } else if (ref === 'mhg') {
        if (urlHotelId) {
          setCurrentStep(BookingStep.LIST)
        } else {
          setCurrentStep(BookingStep.SEARCH)
        }
        setCurrentPaymentStep(1)
      }
    }
  }, [
    addToCart,
    clearCart,
    setCurrentStep,
    setCurrentPaymentStep,
    cancelBooking,
    checkInDate,
    checkOutDate,
    adults,
    kidsOver6,
    kidsUnder6,
    availability,
    isAiChannel,
    setPaymentData,
    // paymentData is intentionally omitted — including it re-runs this effect
    // (and re-applies stale `step`/`ref` URL params) every time createBooking
    // updates paymentData, stomping on nextStep()'s currentPaymentStep change.
  ])

  // Prefill payment data (name/email/phone) from URL params if provided —
  // e.g. a marketing/booking link opened before any booking exists. Kept as
  // its own effect (unlike the big effect above) specifically so it CAN
  // depend on `paymentData` — it needs to know once paymentData has actually
  // resolved from sessionStorage before deciding anything.
  //
  // Guarded on `bookingNo` because Step 2 writes its own `customerName` into
  // the URL via replaceState (so it survives a reload) — without this guard,
  // a plain F5 while at step 2/3 would re-trigger this block and clobber the
  // in-progress booking's paymentData (bookingNo/cardItems/totalAmount...)
  // with a stripped-down object built from just the URL params. Also wait
  // for paymentData to resolve (`!== undefined`) before checking `bookingNo`
  // — while it's still loading we can't yet tell whether a booking is in
  // progress, so treat "unknown" as "assume there might be one" and skip
  // until it resolves.
  useEffect(() => {
    // Wait for paymentData to resolve before deciding anything below.
    if (paymentData === undefined) return

    if (!hasCheckedInitialBookingRef.current) {
      hasCheckedInitialBookingRef.current = true
      if (paymentData?.bookingNo) {
        // bookingNo was already present the very FIRST time paymentData
        // resolved this mount — i.e. it's left over from before this page
        // load (an abandoned/stale in-progress booking, possibly paired
        // with a stale persisted currentPaymentStep of 2/3). Reset to step
        // 1 so the user doesn't land straight into a stale payment.
        //
        // A bookingNo that only appears LATER — e.g. Step 1 creating a
        // fresh booking and moving to step 2, or gen-qr refreshing
        // paymentData while already on step 2/3 — must NOT hit this
        // branch. This effect re-runs on every paymentData change (see
        // deps below), and without this once-only guard it would fire on
        // every such legitimate update too, forcing currentPaymentStep
        // back to 1 right after nextStep() just advanced it — which is
        // exactly what caused Step 1 -> Step 2 to appear stuck and gen-qr
        // to re-fire repeatedly (Step2 mounting, getting reset, mounting
        // again).
        setCurrentPaymentStep(1)
        return
      }
    }

    const urlCustomerName = searchParams.get('customerName')
    const urlCustomerEmail = searchParams.get('customerEmail')
    const urlCustomerPhone = searchParams.get('customerPhone')
    if (!urlCustomerName && !urlCustomerEmail && !urlCustomerPhone) return

    setPaymentData({
      ...(paymentData || { cancelTime: 0 }),
      customerName: urlCustomerName || paymentData?.customerName || '',
      email: urlCustomerEmail || paymentData?.email || '',
      phone: urlCustomerPhone || paymentData?.phone || '',
      cancelTime: paymentData?.cancelTime ?? 0,
    })
  }, [searchParams, paymentData, setPaymentData, setCurrentPaymentStep])

  const renderStepContent = () => {
    switch (bookingStep) {
      case BookingStep.LIST:
      case BookingStep.SEARCH:
        return <BookingSearchListDesktop setTimeIn={setTimeIn} setTimeOut={setTimeOut} />
      case BookingStep.PAYMENT:
        return (
          <PaymentBooking
            hotelId={selectedHotel}
            arrivalDate={checkInDate}
            departureDate={checkOutDate}
            onBackToSearch={() => {
              if (currentPaymentStep === 1) {
                const url = new URL(window.location.href)
                url.searchParams.delete('step')
                window.history.replaceState({}, '', url)
                setCurrentStep(BookingStep.SEARCH)
              } else {
                setCurrentStep(BookingStep.PAYMENT)
              }
            }}
            timeIn={timeIn}
            timeOut={timeOut}
            nights={calculateDurationOfStay(checkInDate, checkOutDate)}
            totalRooms={items.length}
            totalAmount={totalAmount}
          />
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return <OverlayLoading show={true} text={t('search.loadingHotels')} />
  }

  return <div className="mx-auto bg-[#F3F3F3]">{renderStepContent()}</div>
}
